import { metricKeys } from '@pulse/metrics';
import { CURRENT_PRODUCT_MODE } from '@utils/constants';
import { getRangeBuckets } from './engagementBucket';

export enum BUCKETS {
  LifeSessionTimeSecs = 'LifeSessionTimeSecs',
  LifePlayingTime = 'LifePlayingTimeMins',
  LifeJoinTimeSecs = 'LifeJoinTimeSecs',
  IntervalVideoRestartTimeSecs = 'IntervalVideoRestartTimeSecs',
  IntervalRebufferingTimeSecs = 'IntervalRebufferingTimeSecs',
  IntervalNetworkRebufferingTimeSecs = 'IntervalNetworkRebufferingTimeSecs',
  IntervalAvgFramesPerSec = 'IntervalAvgFramesPerSec',
  IntervalAvgBitrateKbps = 'IntervalAvgBitrateKbps',
  IntervalAverageAvgBitrateKbps = 'IntervalAverageAvgBitrateKbps',
  LifeAvgFramesPerSec = 'LifeAvgFramesPerSec',
  LifeNetworkRebufferingEvents = 'LifeNetworkRebufferingEvents',
  LifePodTimebeforePrerollExitSecs = 'LifePodTimebeforePrerollExitSecs',
  LifeJoinTimeMs = 'LifeJoinTimeMs',
  LifeVideoRestartTimeMs = 'LifeVideoRestartTimeMs',
  LifeSessionTimeMs = 'LifeSessionTimeMs',
  LifePlayingTimeMs = 'LifePlayingTimeMs',
  LifePausedTimePct = 'LifePausedTimePct',
  LifePausedTimeSecs = 'LifePausedTimeSecs',
  LifeJoinedAvgBitrate = 'JoinedAvgBitrate',
  LifeAvgNumBitrateSwitches = 'LifeAvgNumBitrateSwitches',
  LifeAverageAvgBitrateKbps = 'LifeAverageAvgBitrateKbps',
  LifeExitRelatedCIRTimeMs = 'LifeExitRelatedCIRTimeMs',
}

export enum DISTRIBUTION {
  ActivePlays = 'ActivePlays',
  ConcurrentPlays = 'ConcurrentPlays',
  ExitBeforeVideoStarts = 'ExitBeforeVideoStarts',
  EndedPlays = 'EndedPlays',
  ExitsDuringPreRoll = 'ExitsDuringPreRoll',
}

const zeroTo10 = getRangeBuckets('0-10', '');
delete zeroTo10['9-10'];

const _METRIC_MAPPING = {
  EI: {
    [metricKeys.VideoStartTime]: {
      metric: metricKeys.Plays,
      dimension: BUCKETS.LifeJoinTimeMs,
      yAxisName: 'Plays',
      title: `Video Start Time vs. Plays`,
      buckets: {
        0: '(-4,0]',
        '0-1': '(0,1000]',
        '1-2': '(1000,2000]',
        '2-3': '(2000,3000]',
        '3-4': '(3000,4000]',
        '4-5': '(4000,5000]',
        '5-6': '(5000,6000]',
        '6-7': '(6000,7000]',
        '7-8': '(7000,8000]',
        '8-9': '(8000,9000]',
        '9-10': '(9000,10000]',
      },
      defaultBucket: '>10',
      unit: 'secs',
    },
    [metricKeys.VideoRestartTime]: {
      metric: DISTRIBUTION.ActivePlays,
      dimension: BUCKETS.LifeVideoRestartTimeMs,
      yAxisName: 'Ended Plays',
      title: `Video Restart Time vs. Ended Plays`,
      buckets: {
        0: '(-4,0]',
        '0-1': '(0,1000]',
        '1-2': '(1000,2000]',
        '2-3': '(2000,3000]',
        '3-4': '(3000,4000]',
        '4-5': '(4000,5000]',
        '5-6': '(5000,6000]',
        '6-7': '(6000,7000]',
        '7-8': '(7000,8000]',
        '8-9': '(8000,9000]',
        '9-10': '(9000,10000]',
      },
      defaultBucket: '>10',
      unit: 'secs',
    },
    [metricKeys.RebufferingRatio]: {
      metric: DISTRIBUTION.ConcurrentPlays,
      dimension: BUCKETS.IntervalRebufferingTimeSecs,
      yAxisName: 'Concurrent Plays',
      title: `Concurrent Plays vs Rebuffering Time`,
      buckets: {
        0: '[0,0]',
        '0-0.5': '(0,500]',
        '0.5-1': '(500,1000]',
        '1-10': '(1000,10000]',
        '10-20': '(10000,20000]',
        '20-30': '(20000,30000]',
        '30-40': '(30000,40000]',
        '40-50': '(40000,50000]',
      },
      defaultBucket: '>50',
      unit: 's',
    },
    [metricKeys.ConnectionInducedRebufferingRatio]: {
      metric: DISTRIBUTION.ActivePlays,
      dimension: BUCKETS.IntervalNetworkRebufferingTimeSecs,
      yAxisName: 'Concurrent Plays',
      title: `Concurrent Plays vs Connection Induced Rebuffering Time`,
      buckets: {
        0: '[0,0]',
        '0-0.5': '(0,500]',
        '0.5-1': '(500,1000]',
        '1-10': '(1000,10000]',
        '10-20': '(10000,20000]',
        '20-30': '(20000,30000]',
        '30-40': '(30000,40000]',
        '40-50': '(40000,50000]',
      },
      defaultBucket: '>50',
      unit: 's',
    },
    [metricKeys.PausedTime]: {
      metric: DISTRIBUTION.EndedPlays,
      dimension: BUCKETS.LifePausedTimeSecs,
      yAxisName: 'Ended Plays',
      title: `Ended Plays vs Paused Time`,
      buckets: {
        0: '[0,0]',
        '0-5': '(0,5]',
        '5-15': '(5,15]',
        '15-30': '[15,30)',
        '30-60': '[30-60)',
        '60-2': '[60-120)',
        '2-5': '[120,300)',
        '5-10': '[300,600)',
        '10-20': '[600,1200)',
      },
      defaultBucket: '>20',
      unit: 'sec',
    },
    [metricKeys.PausedRatio]: {
      metric: DISTRIBUTION.EndedPlays,
      dimension: BUCKETS.LifePausedTimePct,
      yAxisName: 'Ended Plays',
      title: `Ended Plays vs Paused Ratio`,
      buckets: {
        0: '[0,0]',
        '0-0.5': '(0,500]',
        '0.5-1': '(500,1000]',
        '1-10': '(1000,10000]',
        '10-20': '(10000,20000]',
        '20-30': '(20000,30000]',
        '30-40': '(30000,40000]',
        '40-50': '(40000,50000]',
      },
      defaultBucket: '>50',
      unit: 's',
    },
    [metricKeys.NonZeroCIRREndedPlays]: {
      metric: DISTRIBUTION.EndedPlays,
      dimension: BUCKETS.LifeNetworkRebufferingEvents,
      yAxisName: 'Ended Plays',
      title: `Ended Plays vs Connection Induced Interrupted Plays`,
      buckets: BUCKETS.LifeNetworkRebufferingEvents,
      defaultBucket: '>100',
      unit: '',
    },
    [metricKeys.ExitBeforeVideoStarts]: {
      metric: DISTRIBUTION.ExitBeforeVideoStarts,
      dimension: BUCKETS.LifeSessionTimeMs,
      yAxisName: 'Wait Time Before Exits',
      title: `Wait Time Before Exits vs Exit Before Video Starts`,
      buckets: {
        0: '(-4,0]',
        '0-5': '(0,5000]',
        '5-10': '(5000,10000]',
        '10-15': '(10000,15000]',
        '15-20': '(15000,20000]',
        '20-25': '(20000,25000]',
        '25-30': '(25000,30000]',
        '30-35': '(30000,35000]',
        '35-40': '(35000,40000]',
        '40-50': '(40000,50000]',
        '50-60': '(50000,60000]',
      },
      defaultBucket: '>60',
      unit: 'secs',
    },
    [metricKeys.ExitsDuringPreRoll]: {
      metric: DISTRIBUTION.ExitsDuringPreRoll,
      dimension: BUCKETS.LifePodTimebeforePrerollExitSecs,
      yAxisName: 'Wait Time Before Exits',
      title: `Wait Time Before Exits vs Exit During Pre-Roll`,
      buckets: {
        0: '[0,0]',
        '0-5': '(0,5000]',
        '5-10': '(5000,10000]',
        '10-15': '(10000,15000]',
        '15-20': '(15000,20000]',
        '20-25': '(20000,25000]',
        '25-30': '(25000,30000]',
        '30-35': '(30000,35000]',
        '35-40': '(35000,40000]',
        '40-45': '(40000,45000]',
        '45-50': '(45000,50000]',
        '50-55': '(50000,55000]',
        '55-60': '(55000,60000]',
      },
      defaultBucket: '>60',
      unit: 'secs',
    },
    [metricKeys.Bitrate]: {
      metric: DISTRIBUTION.ActivePlays,
      dimension: BUCKETS.IntervalAvgBitrateKbps,
      yAxisName: 'Concurrent Plays',
      title: `Concurrent Plays vs Avg. Peak Bitrate`,
      buckets: {
        0: '[0,0]',
        '0-500': '(0,500]',
        '500-1000': '(500,1000]',
        '1000-1500': '(1000,1500]',
        '1500-2000': '(1500,2000]',
        '2000-3000': '(2000,3000]',
      },
      defaultBucket: '>3000',
      unit: 'kbps',
    },
    [metricKeys.LifeJoinedAvgBitrate]: {
      metric: DISTRIBUTION.EndedPlays,
      dimension: BUCKETS.LifeJoinedAvgBitrate,
      yAxisName: 'Starting Bitrate',
      title: `Starting Bitrate`,
      buckets: {
        0: '(-1,0]',
        '0-200': '(0,200]',
        '200-400': '(200,400]',
        '400-600': '(400,600]',
        '600-800': '(600,800]',
        '800-1000': '(800,1000]',
        '1000-1500': '(1000,1500]',
        '1500-2000': '(1500,2000]',
        '2000-2500': '(2000,2500]',
        '2500-3000': '(2500,3000]',
        '3000-3500': '(3000,3500]',
        '3500-4000': '(3500,4000]',
        '4000-4500': '(4000,4500]',
        '4500-5000': '(4500,5000]',
        '5000-5500': '(5000,5500]',
        '5500-6000': '(5500,6000]',
        '6000-6500': '(6000,6500]',
        '6500-7000': '(6500,7000]',
        '7000-7500': '(7000,7500]',
        '7500-8000': '(7500,8000]',
        '8000-8500': '(8000,8500]',
        '8500-9000': '(8500,9000]',
        '9000-9500': '(9000,9500]',
        '9500-10000': '(9500,10000]',
        '10000-11000': '(10000,11000]',
        '11000-12000': '(11000,12000]',
        '12000-13000': '(12000,13000]',
        '13000-14000': '(13000,14000]',
        '14000-15000': '(14000,15000]',
        '15000-16000': '(15000,16000]',
        '16000-17000': '(16000,17000]',
        '17000-18000': '(17000,18000]',
        '18000-19000': '(18000,19000]',
        '19000-20000': '(19000,20000]',
        '20000-22500': '(20000,22500]',
        '22500-25000': '(22500,25000]',
        '25000-27500': '(25000,27500]',
        '27500-30000': '(27500,30000]',
        '30000-35000': '(30000,35000]',
        '35000-40000': '(35000,40000]',
        '40000-100000': '(40000,100000]',
      },
      defaultBucket: '>100000',
      unit: 'Kbps',
    },
    [metricKeys.LifeAvgNumBitrateSwitches]: {
      metric: DISTRIBUTION.EndedPlays,
      dimension: BUCKETS.LifeAvgNumBitrateSwitches,
      yAxisName: 'Bitrate Switches',
      title: `Bitrate Switches`,
      buckets: {
        0: '[0,0]',
        '0-0.2': '(0,0.2)',
        '0.2-0.4': '[0.2,0.4)',
        '0.4-0.6': '[0.4,0.6)',
        '0.6-0.8': '[0.4,0.8)',
        '0.8-1': '[0.8,1)',
        '1-2': '[1,2)',
        '2-3': '[2,3)',
        '3-4': '[3,4)',
        '4-5': '[4,5)',
        '5-10': '[5,10)',
        '10-50': '[10,50)',
      },
      defaultBucket: '>50',
      unit: '',
    },
    [metricKeys.AvgBitrate]: {
      metric: DISTRIBUTION.EndedPlays,
      dimension: BUCKETS.LifeAverageAvgBitrateKbps,
      yAxisName: 'Ended Plays',
      title: `Avg. Average Bitrate`,
      buckets: {
        0: '[0,0]',
        '0-500': '(0,500]',
        '500-1000': '(500,1000]',
        '1000-1500': '(1000,1500]',
        '1500-2000': '(1500,2000]',
        '2000-3000': '(2000,3000]',
      },
      defaultBucket: '>3000',
      unit: 'Kbps',
    },
    [metricKeys.Framerate]: {
      metric: DISTRIBUTION.ActivePlays,
      dimension: BUCKETS.IntervalAvgFramesPerSec,
      yAxisName: 'Concurrent Plays',
      title: `Concurrent Plays vs Average Framerate`,
      buckets: {
        0: '[0,0]',
        '0-40': '(0,40]',
        '40-80': '(40,80]',
        '80-120': '(80,120]',
        '120-150': '(120,150]',
        '150-200': '(150,200]',
        '200-250': '(200,250]',
      },
      defaultBucket: '>250',
      unit: 'fps',
    },
    [metricKeys.ExitDueToCIR]: {
      metric: metricKeys.ExitDueToCIR,
      dimension: BUCKETS.LifeExitRelatedCIRTimeMs,
      yAxisName: 'CIR Related Exits',
      title: `CIR Related Exits vs CIR Time preceding Exit`,
      buckets: {
        0: '[0,0]',
        '0-0.5': '(0,500]',
        '0.5-1': '(500,1000]',
        '1-10': '(1000,10000]',
        '10-20': '(10000,20000]',
        '20-30': '(20000,30000]',
        '30-40': '(30000,40000]',
        '40-50': '(40000,50000]',
      },
      defaultBucket: '>50',
      unit: 's',
    },
  },
  AI: {},
  APP: {},
};

// not applicable to AI as now
export const DISTRIBUTION_METRICS_MAPPING = _METRIC_MAPPING[CURRENT_PRODUCT_MODE];
